import { RouteData } from '@cya-routes/shared'

export function generateGPX(route: RouteData): string {
  const now = new Date().toISOString()
  
  // GPX header
  let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="CYA Routes" 
  xmlns="http://www.topografix.com/GPX/1/1" 
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(route.name)}</name>
    <time>${now}</time>
  </metadata>
  <trk>
    <name>${escapeXml(route.name)}</name>
    <trkseg>
`

  // Add track points with elevation
  if (route.coordinates && route.elevation?.profile) {
    // Match coordinates with elevation data
    const elevationMap = new Map<number, number>()
    
    // Build elevation lookup by distance
    route.elevation.profile.forEach(point => {
      elevationMap.set(point.distance, point.elevation)
    })
    
    // Calculate cumulative distance for each coordinate
    let cumulativeDistance = 0
    route.coordinates.forEach((coord, index) => {
      const [lon, lat] = coord
      
      // Calculate distance from start
      if (index > 0) {
        const [prevLon, prevLat] = route.coordinates[index - 1]
        const segmentDistance = calculateDistance(prevLat, prevLon, lat, lon)
        cumulativeDistance += segmentDistance
      }
      
      // Find closest elevation point by distance
      const distanceKm = cumulativeDistance / 1000
      let elevation = 0
      
      // Find the closest elevation value
      let closestDistance = Infinity
      elevationMap.forEach((ele, dist) => {
        if (Math.abs(dist - distanceKm) < closestDistance) {
          closestDistance = Math.abs(dist - distanceKm)
          elevation = ele
        }
      })
      
      // If no close match, interpolate between points
      if (closestDistance > 0.1 && route.elevation.profile.length > 1) {
        for (let i = 0; i < route.elevation.profile.length - 1; i++) {
          const p1 = route.elevation.profile[i]
          const p2 = route.elevation.profile[i + 1]
          
          if (distanceKm >= p1.distance && distanceKm <= p2.distance) {
            // Linear interpolation
            const ratio = (distanceKm - p1.distance) / (p2.distance - p1.distance)
            elevation = p1.elevation + (p2.elevation - p1.elevation) * ratio
            break
          }
        }
      }
      
      gpx += `      <trkpt lat="${lat}" lon="${lon}">
        <ele>${elevation.toFixed(1)}</ele>
      </trkpt>
`
    })
  } else if (route.coordinates) {
    // No elevation data, just export coordinates
    route.coordinates.forEach(coord => {
      const [lon, lat] = coord
      gpx += `      <trkpt lat="${lat}" lon="${lon}">
        <ele>0</ele>
      </trkpt>
`
    })
  }

  // Close GPX
  gpx += `    </trkseg>
  </trk>
</gpx>`

  return gpx
}

// Helper function to escape XML special characters
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

// Helper function to calculate distance between two coordinates
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3 // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// Function to trigger GPX download
export function downloadGPX(route: RouteData): void {
  const gpxContent = generateGPX(route)
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' })
  const url = URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = url
  link.download = `${route.name.replace(/[^a-zA-Z0-9]/g, '_')}.gpx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  URL.revokeObjectURL(url)
}